import React, { useEffect, useMemo, useState } from 'react';
import { useFetchPovItemsForProduct, useFormUpdatePovTestCaseMutation } from '../../../../hooks/opportunity-pov-hooks';
import { HbrAccordion, HbrCard, HbrOption, HbrSelect, HbrSpinner, HbrTextarea } from '@harbor/elements-react-standalone';
import StbButton from '../../../security-toolbox-ui/StbButton';
import { useParams } from 'react-router-dom';
import { useFetchOpportunity } from '../../../../hooks/opportunity-hooks';
import { OpportunityProduct } from '../../../../types/opportunity';
import { asSingleValue } from '../../../../support/utils';

enum ValidationMethod {
  Validated = 'validated',
  Workaround = 'workaround',
  NoWorkaround = 'no-workaround',
  UpcomingFeature = 'upcoming-feature'
}

const validationTypeSelection = [
  { label: 'Validated', value: ValidationMethod.Validated },
  { label: 'Workaround', value: ValidationMethod.Workaround },
  { label: 'No Workaround', value: ValidationMethod.NoWorkaround },
  { label: 'Upcoming Feature', value: ValidationMethod.UpcomingFeature }
];

interface TestCaseWithChange {
  description: string;
  id: number;
  notes: string;
  outcome_id: number;
  success: boolean;
  title: string;
  usecase_id: number;
  validation: ValidationMethod | null;
  updated: boolean;
  product_feature_success_criteria?: number | null;
  is_custom?: boolean;
}

const accordionStyles = `
  flex
  flex-row
  gap-4
  justify-between
  align-center 
  w-full
`;

const ValidatePhaseValidation: React.FC<{ selectedProductId: OpportunityProduct['id'] }> = ({ selectedProductId }) => {
  const { id: opportunityId } = useParams();
  const { data: opportunity } = useFetchOpportunity(opportunityId);

  const {
    data: outcomes,
    isLoading: isLoadingOutcomes,
    isFetching: isFetchingOutcomes
  } = useFetchPovItemsForProduct(
    opportunityId,
    selectedProductId,
    { enabled: !!opportunity && !!selectedProductId }
  );

  const { mutateAsync: updateTestCase, isPending: isLoading } = useFormUpdatePovTestCaseMutation(opportunityId);

  const [editing, setEditing] = useState<Record<string, TestCaseWithChange>>({});
  const [pendingSave, setPendingSave] = useState<number | null>(null);

  const visibleOutcomes = useMemo(() => {
    return (outcomes ?? []).filter((outcome) => (outcome?.usecases?.length ?? 0) > 0);
  }, [outcomes]);

  useEffect(() => {
    if (pendingSave && !isFetchingOutcomes && !isLoading) {
      setEditing(prev => {
        const copy = { ...prev };
        delete copy[pendingSave];
        return copy;
      });
      setPendingSave(null);
    }
  }, [pendingSave, isFetchingOutcomes, isLoading, outcomes]);

  const handleInputChange = (
    property: string,
    value: string,
    testCase: TestCaseWithChange,
  ) => {
    setEditing(prev => ({
      ...prev,
      [testCase.id]: {
        ...prev[testCase.id],
        ...(Object.keys(prev).includes(String(testCase.id)) ? prev[testCase.id] : testCase),
        [property]: value,
        updated: true,
      }
    }));
  };

  const handleClickSave = async (testCase: TestCaseWithChange) => {
    const localEdit = editing[testCase.id] || testCase;

    const isSuccess =
      localEdit.validation === ValidationMethod.Validated ||
      localEdit.validation === ValidationMethod.Workaround;

    const updatedCase = {
      ...localEdit,
      pov_usecase: localEdit.usecase_id,
      success: isSuccess,
      product_feature_success_criteria: localEdit.product_feature_success_criteria ?? null,
      is_custom: localEdit.is_custom ?? false,
    };

    await updateTestCase(updatedCase);
    setPendingSave(localEdit.id);
  };

  if (isLoadingOutcomes && !outcomes) {
    return <HbrSpinner size="large">Loading...</HbrSpinner>;
  }

  return (
    <>
      {visibleOutcomes.length === 0 ? (
        <span className="text-sm font-normal italic">No outcomes found</span>
      ) : (
        visibleOutcomes.map(outcome => (
          <HbrAccordion key={outcome.id} variant="stacked" className="mb-2">
            <div slot="label" className={accordionStyles}>
              <div className="text-base font-semibold">{outcome?.title}</div>
            </div>

            <div className="flex flex-col w-full gap-4">
              {outcome.usecases.map(useCase => (
                <div key={useCase.id} className="flex flex-col gap-2">
                  <div className="font-semibold">{useCase.title}</div>

                  {useCase?.testcases?.length === 0 ? (
                    <span className="text-sm font-normal italic">No test cases found</span>
                  ) : (
                    useCase.testcases.map(testCase => {
                      const localEdit = editing[testCase.id] || testCase;
                      return (
                        <HbrCard
                          key={testCase.id}
                          container={true}
                          className="mb-2"
                          style={{ '--width': '100%' } as React.CSSProperties}
                        >
                          <div className="flex flex-col">
                            <div className="flex flex-row w-full gap-4 justify-between align-center mb-2">
                              <div className="flex-1 grow text-sm font-semibold">{testCase.title}</div>
                              <HbrSelect
                                placeholder="Select Validation Option"
                                value={localEdit.validation as ValidationMethod}
                                onHbr-change={e =>
                                  handleInputChange('validation', asSingleValue(e.target.value), localEdit)
                                }
                              >
                                {validationTypeSelection.map(option => (
                                  <HbrOption key={option.value} value={option.value}>
                                    {option.label}
                                  </HbrOption>
                                ))}
                              </HbrSelect>
                            </div>

                            <HbrTextarea
                              autoFocus
                              value={localEdit.notes}
                              placeholder="Add additional test case notes"
                              onHbr-input={e => handleInputChange('notes', e.target.value, localEdit)}
                              className="mb-2"
                            />

                            {localEdit?.updated && (
                              <div className="flex justify-end gap-2">
                                <StbButton
                                  role="save"
                                  onClick={() => handleClickSave(localEdit)}
                                  loading={isLoading}
                                />
                              </div>
                            )}
                          </div>
                        </HbrCard>
                      );
                    })
                  )}
                </div>
              ))}
            </div>
          </HbrAccordion>
        ))
      )}
    </>
  );
};

export default ValidatePhaseValidation;